import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createClientRateLimit } from "../../lib/clientRateLimit";

import {
  type CityRequestCounts,
  type SubmitCityRequestArgs,
  fetchCityRequestCounts,
  submitCityRequest,
} from "./api";

const COUNTS_KEY = ["city-request-counts"] as const;
const rateLimit = createClientRateLimit(
  "voltaroute.city-requests.lastBySlug",
  24 * 60 * 60 * 1000,
);

export class RateLimitedError extends Error {
  constructor() {
    super("RATE_LIMITED");
    this.name = "RateLimitedError";
  }
}

export function useCityRequestCounts() {
  return useQuery<CityRequestCounts>({
    queryKey: COUNTS_KEY,
    queryFn: fetchCityRequestCounts,
    staleTime: 60_000,
  });
}

export function useSubmitCityRequest() {
  const qc = useQueryClient();
  return useMutation<void, Error, SubmitCityRequestArgs>({
    mutationFn: async (args) => {
      if (!rateLimit.canRequest(args.citySlug)) {
        throw new RateLimitedError();
      }
      await submitCityRequest(args);
      rateLimit.mark(args.citySlug);
    },
    onSuccess: (_data, vars) => {
      qc.setQueryData<CityRequestCounts>(COUNTS_KEY, (prev) => {
        const next: CityRequestCounts = { ...(prev ?? {}) };
        next[vars.citySlug] = (next[vars.citySlug] ?? 0) + 1;
        return next;
      });
    },
  });
}
